import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'
import { useQrStore } from '../store/useQrStore'
import type { QrCode } from '../store/useQrStore'
import { useI18n } from '../i18n'
import { Meta } from '../components/Meta'
import { CopyField } from '../components/CopyField'

const REDIRECT_BASE = 'https://qr-api.masterlevel.de/r/'

export function MyCodesPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const { codes, isLoading, fetchAll, update, remove } = useQrStore()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editUrl, setEditUrl] = useState('')
  const [editTitle, setEditTitle] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    void fetchAll()
  }, [user, fetchAll, navigate])

  const startEdit = (code: QrCode) => {
    setEditingId(code.id)
    setEditUrl(code.target_url)
    setEditTitle(code.title)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditUrl('')
    setEditTitle('')
  }

  const saveEdit = async () => {
    if (!editingId) return
    setSaving(true)
    await update(editingId, { target_url: editUrl, title: editTitle })
    setSaving(false)
    setEditingId(null)
  }

  const confirmDelete = async (id: string) => {
    await remove(id)
    setConfirmDeleteId(null)
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center text-text-muted">
        {t('general.loading')}
      </div>
    )
  }

  return (
    <>
    <Meta title={t('meta.myCodes.title')} description={t('meta.myCodes.description')} path="/my-codes" />
    <div className="mx-auto w-full max-w-3xl px-4 py-8">
      <h2 className="mb-6 text-xl font-bold text-text">{t('myCodes.title')}</h2>

      {codes.length === 0 && (
        <p className="text-text-muted">{t('myCodes.empty')}</p>
      )}

      <div className="flex flex-col gap-4">
        {codes.map((code) => (
          <div key={code.id} className="rounded-xl bg-surface-light p-5">
            {editingId === code.id ? (
              <div className="flex flex-col gap-3">
                <input
                  type="text"
                  placeholder={t('myCodes.titlePlaceholder')}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="rounded-lg border-2 border-surface-lighter bg-surface px-4 py-2 text-sm text-text outline-none focus:border-primary"
                />
                <input
                  type="url"
                  placeholder="https://..."
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  className="rounded-lg border-2 border-surface-lighter bg-surface px-4 py-2 text-sm text-text outline-none focus:border-primary"
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveEdit}
                    disabled={saving || !editUrl}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-40"
                  >
                    {saving ? t('general.loading') : t('account.save')}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="rounded-lg bg-surface px-4 py-2 text-sm text-text-muted hover:text-text"
                  >
                    {t('general.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-text">
                    {code.title || code.short_code}
                  </p>
                  <p className="truncate text-sm text-text-muted">{code.target_url}</p>
                  <CopyField value={REDIRECT_BASE + code.short_code} className="mt-2 max-w-md" />
                  <p className="mt-1 text-xs text-text-muted">
                    {t('myCodes.scans')}: <span className="font-semibold text-primary">{code.scan_count}</span>
                    {' — '}{new Date(code.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => startEdit(code)}
                    className="rounded-lg bg-surface px-3 py-1.5 text-xs text-text-muted hover:text-text transition-colors"
                  >
                    {t('myCodes.edit')}
                  </button>
                  {confirmDeleteId === code.id ? (
                    <>
                      <button
                        onClick={() => confirmDelete(code.id)}
                        className="rounded-lg bg-error px-3 py-1.5 text-xs text-white hover:opacity-80"
                      >
                        {t('myCodes.confirmDelete')}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="rounded-lg bg-surface px-3 py-1.5 text-xs text-text-muted hover:text-text"
                      >
                        {t('general.cancel')}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(code.id)}
                      className="rounded-lg bg-surface px-3 py-1.5 text-xs text-error hover:opacity-80 transition-opacity"
                    >
                      {t('myCodes.delete')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    </>
  )
}
